import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import { ExpertChatView } from "@/components/expert-audio/ExpertChatView";
import { PushPrivateThread } from "@/components/business-push/PushPrivateThread";
import { ProfileForm } from "@/components/account/profile-form";
import { loadAccountOrdersPayload, loadAccountProfilePayload, type CustomerOrderRow } from "@/lib/account-data";
import { loadAccountOrderExpertContext, loadAccountOrderPushContext } from "@/lib/order-context";
import { orderHasBusinessPush, orderHasExpertAudit } from "@/lib/expert-line-items";
import { useSession } from "@/session/session-context";

const UUID_RE = /^[0-9a-f-]{36}$/i;

function formatMoney(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function statusLabel(status: string): string {
  switch (status) {
    case "paid":
      return "Paid";
    case "processing":
      return "Processing";
    case "fulfilled":
      return "Delivered";
    case "refunded":
      return "Refunded";
    default:
      return status;
  }
}

function isAbsorbedOrder(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return false;
  return Boolean((metadata as Record<string, unknown>).absorbed_into_order_id);
}

function expertStatusCustomerLabel(status: string | undefined): string {
  if (!status) return "Setting up…";
  if (status === "awaiting_audio") return "Awaiting your voice memo";
  if (status === "audio_submitted") return "Audio submitted";
  if (status === "writeup_ready") return "Writeup ready";
  return status;
}

export function AccountIndexRedirect() {
  return <Navigate to="/account/profile" replace />;
}

export function AccountProfileScreen() {
  const { user, loading } = useSession();
  const [profilePayload, setProfilePayload] = useState<{
    email: string;
    profile: {
      display_name: string | null;
      phone: string | null;
      company_name: string | null;
      notes: string | null;
    };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    void loadAccountProfilePayload().then((j) => {
      if (!j) setError("Could not load profile.");
      else setProfilePayload(j);
    });
  }, [user?.id]);

  if (loading) {
    return <p className="text-sm text-zinc-600">Loading…</p>;
  }
  if (!user?.id) {
    return null;
  }
  if (error) {
    return <p className="text-sm text-rose-600">{error}</p>;
  }
  if (!profilePayload) {
    return <p className="text-sm text-zinc-600">Loading profile…</p>;
  }

  return (
    <div>
      <h1 className="text-2xl font-black tracking-tight text-zinc-900">My profile</h1>
      <p className="mt-2 text-sm text-zinc-600">
        Your email comes from sign-in. Add optional details so we know how to address you and reach you for fulfillments.
      </p>
      <ProfileForm
        email={profilePayload.email}
        initial={{
          display_name: profilePayload.profile.display_name ?? "",
          phone: profilePayload.profile.phone ?? "",
          company_name: profilePayload.profile.company_name ?? "",
          notes: profilePayload.profile.notes ?? "",
        }}
      />
    </div>
  );
}

export function AccountOrdersScreen() {
  const { user, loading: sessionLoading } = useSession();
  const [orders, setOrders] = useState<CustomerOrderRow[] | null>(null);
  const [expertStatusByOrder, setExpertStatusByOrder] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    void loadAccountOrdersPayload().then((j) => {
      if (!j) setError("Could not load orders.");
      else {
        setOrders(j.orders);
        setExpertStatusByOrder(j.expertStatusByOrder ?? {});
      }
    });
  }, [user]);

  if (sessionLoading) {
    return <p className="text-sm text-zinc-600">Loading…</p>;
  }
  if (!user) {
    return null;
  }
  if (error) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        {error} If you just deployed, apply the latest Supabase migration for{" "}
        <code className="rounded bg-white/80 px-1">customer_orders</code> and set{" "}
        <code className="rounded bg-white/80 px-1">STRIPE_WEBHOOK_SECRET</code>.
      </div>
    );
  }
  if (!orders) {
    return <p className="text-sm text-zinc-600">Loading orders…</p>;
  }

  const visibleOrders = orders.filter((o) => !isAbsorbedOrder(o.metadata));

  return (
    <div>
      <h1 className="text-2xl font-black tracking-tight text-zinc-900">My orders</h1>
      <p className="mt-2 text-sm text-zinc-600">
        Purchases from your Do or Dump add-ons appear here after Stripe confirms payment (webhook). Expert reviews use the voice memo
        link; PDF and writeup unlock when ready. Business Push (if added) uses a private in-app thread on the same order.
      </p>

      {!visibleOrders.length ? (
        <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm font-medium text-zinc-700">No orders yet.</p>
          <p className="mt-2 text-xs text-zinc-500">
            After you buy an add-on from your report, payment creates a row here within a few seconds.
          </p>
          <Link
            to="/dashboard"
            className="mt-4 inline-block text-sm font-bold text-violet-700 underline-offset-2 hover:underline"
          >
            Back to report
          </Link>
        </div>
      ) : (
        <ul className="mt-8 space-y-4">
          {visibleOrders.map((o) => {
            const hasExpert = orderHasExpertAudit(o.line_items);
            const hasPush = orderHasBusinessPush(o.line_items);
            const exStatus = expertStatusByOrder[o.id];
            return (
              <li key={o.id} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      {new Date(o.created_at).toLocaleString()}
                    </p>
                    <p className="mt-1 font-bold text-zinc-900">{o.line_summary || "Do or Dump add-on"}</p>
                    <p className="mt-1 text-xs text-zinc-500">{o.customer_email}</p>
                    {hasExpert ? (
                      <div className="mt-3 rounded-lg bg-violet-50 px-3 py-2 text-xs">
                        <p className="font-bold text-violet-900">Expert audit</p>
                        <p className="mt-1 text-violet-800">{expertStatusCustomerLabel(exStatus)}</p>
                        <Link
                          to={`/account/orders/${o.id}/expert`}
                          className="mt-2 inline-block font-bold text-violet-700 underline-offset-2 hover:underline"
                        >
                          Open chat thread →
                        </Link>
                      </div>
                    ) : null}
                    {hasPush ? (
                      <div className="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-xs">
                        <p className="font-bold text-sky-950">30-Day Business Push</p>
                        <p className="mt-1 text-sky-900">Private async thread — questions & check-ins here.</p>
                        <Link
                          to={`/account/orders/${o.id}/push`}
                          className="mt-2 inline-block font-bold text-sky-800 underline-offset-2 hover:underline"
                        >
                          Open private thread →
                        </Link>
                      </div>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-black tabular-nums text-zinc-900">
                      {formatMoney(o.amount_total_cents, o.currency)}
                    </p>
                    <span
                      className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                        o.status === "fulfilled"
                          ? "bg-emerald-100 text-emerald-900"
                          : o.status === "processing"
                            ? "bg-amber-100 text-amber-900"
                            : o.status === "refunded"
                              ? "bg-zinc-200 text-zinc-800"
                              : "bg-violet-100 text-violet-900"
                      }`}
                    >
                      {statusLabel(o.status)}
                    </span>
                  </div>
                </div>

                {o.fulfillment_note ? (
                  <p className="mt-3 border-t border-zinc-100 pt-3 text-sm text-zinc-700">{o.fulfillment_note}</p>
                ) : null}

                {o.delivery_url ? (
                  <a
                    href={o.delivery_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex text-sm font-bold text-violet-700 underline-offset-2 hover:underline"
                  >
                    Open delivery link
                  </a>
                ) : !hasExpert && !hasPush && o.status !== "fulfilled" ? (
                  <p className="mt-3 text-xs text-zinc-500">
                    We&apos;ll email you when your purchase is ready, and any download link will appear here.
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

type ExpertContext = {
  orderId: string;
  lineSummary: string;
  customerEmail: string;
  hasBusinessPush: boolean;
  expertStatus: "awaiting_audio" | "audio_submitted" | "writeup_ready" | null;
  writeupMarkdown: string | null;
  audioSubmittedAt: string | null;
  writeupReadyAt: string | null;
  audioDurationSeconds: number | null;
};

export function AccountExpertScreen() {
  const { user } = useSession();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState<ExpertContext | null>(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    if (!orderId || !UUID_RE.test(orderId)) {
      setGone(true);
      return;
    }
    if (!user?.id) {
      navigate("/dashboard", { replace: true });
      return;
    }
    void loadAccountOrderExpertContext(orderId).then((j) => {
      if (!j) {
        navigate("/account/orders", { replace: true });
        return;
      }
      setCtx(j);
    });
  }, [orderId, navigate, user?.id]);

  if (!orderId || !UUID_RE.test(orderId) || gone) {
    return <p className="text-sm text-zinc-600">Not found.</p>;
  }
  if (!ctx) {
    return <p className="text-sm text-zinc-600">Loading…</p>;
  }

  return (
    <div className="pb-4 pt-2">
      <ExpertChatView
        orderId={ctx.orderId}
        lineSummary={ctx.lineSummary}
        customerEmail={ctx.customerEmail}
        hasBusinessPush={ctx.hasBusinessPush}
        expertStatus={ctx.expertStatus}
        writeupMarkdown={ctx.writeupMarkdown}
        audioSubmittedAt={ctx.audioSubmittedAt}
        writeupReadyAt={ctx.writeupReadyAt}
        audioDurationSeconds={ctx.audioDurationSeconds}
      />
    </div>
  );
}

export function AccountPushScreen() {
  const { user } = useSession();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState<{ orderId: string; lineSummary: string } | null>(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    if (!orderId || !UUID_RE.test(orderId)) {
      setGone(true);
      return;
    }
    if (!user?.id) {
      navigate("/dashboard", { replace: true });
      return;
    }
    void loadAccountOrderPushContext(orderId).then((j) => {
      if (!j) {
        navigate("/account/orders", { replace: true });
        return;
      }
      setCtx(j);
    });
  }, [orderId, navigate, user?.id]);

  if (!orderId || !UUID_RE.test(orderId) || gone) {
    return <p className="text-sm text-zinc-600">Not found.</p>;
  }
  if (!ctx) {
    return <p className="text-sm text-zinc-600">Loading…</p>;
  }

  return (
    <div className="pb-4 pt-2">
      <PushPrivateThread
        orderId={ctx.orderId}
        viewer="buyer"
        lineSummary={ctx.lineSummary}
        backHref="/account/orders"
        backLabel="Orders"
      />
    </div>
  );
}
